import { useState, useEffect, useMemo, useRef } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import axiosInstance from '../../lib/axios';
import {
    Box, Typography, CircularProgress, Tooltip, IconButton, Button, Stack,
    Table, TableHead, TableBody, TableFooter, TableRow, TableCell, TableContainer,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { selectGlobalProperty, selectProperties } from '../../redux/propertySlice';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import HomeIcon from '@mui/icons-material/Home';
import PrintIcon from '@mui/icons-material/Print';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import CollectionPrint from './CollectionPrint';
import { useReactToPrint } from 'react-to-print';

const formatAmount = (amount) => Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Blank / null remark is grouped under "No Remark" (label added by the code, not the API)
const getRemarkName = (receipt) => String(receipt.Remark || '').trim() || 'No Remark';

const getFinancialYear = () => {
    const today = dayjs();
    const startYear = today.month() >= 3 ? today.year() : today.year() - 1;
    return { start: dayjs(`${startYear}-04-01`), end: dayjs(`${startYear + 1}-03-31`) };
};

// Months inside the current financial year show the full name (September).
// Older / other months show the short name with a 2-digit year (Jan-25).
const getMonthLabel = (date) => {
    const { start, end } = getFinancialYear();
    const isInCurrentFinancialYear = !date.isBefore(start, 'month') && !date.isAfter(end, 'month');
    return isInCurrentFinancialYear ? date.format('MMMM') : date.format('MMM-YY');
};

// rows = months, columns = remarks
const buildSummary = (receipts, fromDate, toDate) => {
    const remarkNames = [...new Set(receipts.map(getRemarkName))].sort((a, b) => a.localeCompare(b));
    const monthMap = {};

    const addMonth = (date) => {
        const monthKey = date.format('YYYY-MM');
        if (!monthMap[monthKey]) {
            monthMap[monthKey] = { monthKey, monthLabel: getMonthLabel(date), amountByRemark: {}, total: 0 };
        }
        return monthMap[monthKey];
    };

    // every month of the selected range, even if it has no data
    let month = fromDate.startOf('month');
    while (!month.isAfter(toDate)) {
        addMonth(month);
        month = month.add(1, 'month');
    }

    receipts.forEach((receipt) => {
        if (!receipt.RecDt) return;
        const monthRow = addMonth(dayjs(receipt.RecDt));
        const remarkName = getRemarkName(receipt);
        const amount = Number(receipt.RcdAmt) || 0;
        monthRow.amountByRemark[remarkName] = (monthRow.amountByRemark[remarkName] || 0) + amount;
        monthRow.total += amount;
    });

    const monthRows = Object.values(monthMap).sort((a, b) => a.monthKey.localeCompare(b.monthKey));

    const grandTotal = { amountByRemark: {}, total: 0 };
    monthRows.forEach((monthRow) => {
        remarkNames.forEach((name) => {
            grandTotal.amountByRemark[name] = (grandTotal.amountByRemark[name] || 0) + (monthRow.amountByRemark[name] || 0);
        });
        grandTotal.total += monthRow.total;
    });

    return { monthRows, remarkNames, grandTotal };
};

const cell = { fontSize: 12.5, padding: '5px 5px', borderBottom: '1px solid #d6d4d4', borderRight: '1px solid #d6d4d4', color: '#000', lineHeight: 1.2 };
const headCell = { ...cell, fontSize: 12, fontWeight: 700, padding: '6px 10px', bgcolor: '#f5f5f5', borderBottom: '1px solid #000', whiteSpace: 'nowrap' };
const footCell = { ...cell, fontWeight: 700, padding: '7px 10px', bgcolor: '#f5f5f5', borderTop: '1px solid #000', position: 'sticky', bottom: 0, zIndex: 3, whiteSpace: 'nowrap' };
const stickyLeft = { position: 'sticky', left: 0 };
const stickyRight = { position: 'sticky', right: 0, backgroundColor: '#fff', };
const totalBorder = { borderLeft: '1px solid #000', borderRight: 'none' };
const number = { fontVariantNumeric: 'tabular-nums' };

const datePickerSx = {
    flex: { xs: '0 1 48%', md: '0 1 160px' },
    bgcolor: 'white',
    borderRadius: 2,
    '& .MuiInputBase-root': { fontSize: '1rem', py: 0.2 },
    '& .MuiOutlinedInput-input': { padding: '4px 8px' },
};

const CollectionSummary = () => {
    const navigate = useNavigate();
    const printRef = useRef(null);
    const globalProperty = useSelector(selectGlobalProperty);
    const properties = useSelector(selectProperties);
    const propertyName = properties.find((property) => Number(property.Id) === Number(globalProperty))?.Name;
    const [receipts, setReceipts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [fromDate, setFromDate] = useState(() => getFinancialYear().start);
    const [toDate, setToDate] = useState(() => getFinancialYear().end);
    const [appliedFromDate, setAppliedFromDate] = useState(fromDate);
    const [appliedToDate, setAppliedToDate] = useState(toDate);

    const fetchCollectionSummary = async () => {
        if (!fromDate || !toDate) return;

        setIsLoading(true);

        try {
            const response = await axiosInstance.post('TrnRctVchr/GetTrnRctVchrFilterDashBoard', {
                PageNumber: 1,
                PageSize: 100000,
                SearchText: '',
                PropId: globalProperty,
                SearchFrom: fromDate.format('YYYY-MM-DD'),
                SearchTo: toDate.format('YYYY-MM-DD'),
                Flag: 'RctRpt',
            });

            const result = response.data;
            const list = result?.Data ?? [];

            if (result?.Status === 0 && Array.isArray(list)) {
                setReceipts(list);
                setAppliedFromDate(fromDate);
                setAppliedToDate(toDate);
            } else {
                setReceipts([]);
            }

        } catch (error) {
            toast.error('Error while loading summary.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (globalProperty) fetchCollectionSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalProperty]);

    const { monthRows, remarkNames, grandTotal } = useMemo(
        () => buildSummary(receipts, appliedFromDate, appliedToDate),
        [receipts, appliedFromDate, appliedToDate]
    );

    const handleExport = async () => {
        if (!receipts.length) return toast.info('No data to export.');

        const HEADER_ROW = 1;
        const totalColumns = remarkNames.length + 2; // Month + remarks + Total
        const amountFormat = '#,##0.00;-#,##0.00;"-"'; // zero shows as "-"

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Collection Summary', {
            views: [{ state: 'frozen', xSplit: 1, ySplit: HEADER_ROW }],
        });

        const thinBorder = {
            top: { style: 'thin', color: { argb: 'FFBFBFBF' } },
            left: { style: 'thin', color: { argb: 'FFBFBFBF' } },
            bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } },
            right: { style: 'thin', color: { argb: 'FFBFBFBF' } },
        };

        // Fixed column widths
        sheet.columns = [
            { key: 'month', width: 14 },
            ...remarkNames.map((name) => ({ key: name, width: 16 })),
            { key: 'total', width: 16 },
        ];

        // Header row: the only colored row
        const headerRow = sheet.getRow(HEADER_ROW);
        headerRow.values = ['Month', ...remarkNames, 'Total'];
        headerRow.height = 22;
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF635BFF' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = thinBorder;
        });

        // One row per month
        monthRows.forEach((monthRow) => {
            const row = sheet.addRow([
                monthRow.monthLabel,
                ...remarkNames.map((name) => monthRow.amountByRemark[name] || 0),
                monthRow.total,
            ]);
            row.eachCell((cell, colNumber) => {
                cell.border = thinBorder;
                if (colNumber === 1) {
                    cell.font = { bold: true };
                } else {
                    cell.numFmt = amountFormat;
                    cell.alignment = { horizontal: 'right' };
                }
            });
            row.getCell(totalColumns).font = { bold: true };
        });

        // Grand Total row: bold only, no color
        const totalRow = sheet.addRow([
            'Grand Total',
            ...remarkNames.map((name) => grandTotal.amountByRemark[name] || 0),
            grandTotal.total,
        ]);
        totalRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true };
            cell.border = { ...thinBorder, top: { style: 'medium', color: { argb: 'FF000000' } } };
            if (colNumber > 1) {
                cell.numFmt = amountFormat;
                cell.alignment = { horizontal: 'right' };
            }
        });

        const fileBuffer = await workbook.xlsx.writeBuffer();
        saveAs(
            new Blob([fileBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
            `Collection_Summary_${fromDate.format('DDMMYYYY')}_${toDate.format('DDMMYYYY')}.xlsx`
        );
    };

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: `Collection_Summary_${appliedFromDate.format('DDMMYYYY')}_${appliedToDate.format('DDMMYYYY')}`,
    });

    return (
        <>
            <ToastContainer />
            <Box
                sx={{
                    width: '100%',
                    height: '90vh',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    py: 2,
                    backgroundColor: 'rgb(249, 249, 249)',
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr)',
                    gridTemplateRows: 'auto minmax(0, 1fr)',
                }}
            >
                <Box
                    sx={{
                        bgcolor: '#fff',
                        p: { xs: '6px 14px', sm: '8px 16px' },
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                        borderRadius: '12px',
                        mb: 2,
                        width: '95%',
                        justifySelf: 'center',
                        boxSizing: 'border-box',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 1.5,
                    }}
                >
                    <Breadcrumbs separator="›" sx={{ fontSize: { xs: 14, sm: 16 } }}>
                        <Link underline="hover" color="#635bff" sx={{ cursor: 'pointer', fontWeight: 500, display: 'flex', alignItems: 'center' }}
                            onClick={() => navigate('/dashboard')}>
                            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" /> Home
                        </Link>
                        <Link underline="hover" color="#635bff" sx={{ cursor: 'pointer', fontWeight: 500 }}
                            onClick={() => navigate('/reports/accountsFinance')}>
                            Account & Finance
                        </Link>
                        <Typography color="text.primary" sx={{ fontWeight: 500 }}>Collection Summary</Typography>
                    </Breadcrumbs>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
                            <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', md: 'auto' }, justifyContent: 'space-between' }}>
                                <DatePicker
                                    label="From Date"
                                    value={fromDate}
                                    onChange={setFromDate}
                                    maxDate={toDate}
                                    format="DD/MM/YYYY"
                                    slotProps={{ textField: { size: 'small', sx: datePickerSx } }}
                                />
                                <DatePicker
                                    label="To Date"
                                    value={toDate}
                                    onChange={setToDate}
                                    minDate={fromDate}
                                    format="DD/MM/YYYY"
                                    slotProps={{ textField: { size: 'small', sx: datePickerSx } }}
                                />
                            </Stack>

                            <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
                                <Tooltip title="Retrieve data" arrow>
                                    <span>
                                        <Button variant="contained"
                                            disabled={!fromDate || !toDate || isLoading}
                                            onClick={fetchCollectionSummary}
                                            sx={{ height: 30, bgcolor: '#635bff', fontSize: '0.8rem', borderRadius: 2, textTransform: 'none', px: 2, '&:hover': { bgcolor: '#635bff' } }}>
                                            Get Data
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Export to excel" arrow>
                                    <Button variant="outlined" color="success" startIcon={<ImportExportIcon />} onClick={handleExport}
                                        sx={{ height: 30, fontSize: '0.8rem', borderRadius: 2, textTransform: 'none', px: 2 }}>
                                        Export
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Print Records" arrow>
                                    <IconButton onClick={handlePrint} color='primary'
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            border: '1px solid #2a68b9',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <PrintIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Previous Page" arrow>
                                    <IconButton onClick={() => navigate('/reports/accountsFinance')}
                                        sx={{ width: 32, height: 32, color: '#fff', bgcolor: '#635bff', ':hover': { bgcolor: '#635bff' } }}>
                                        <ArrowBackIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Move to Collection Report" arrow>
                                    <IconButton onClick={() => navigate('/receipt-report')}
                                        sx={{ width: 32, height: 32, color: '#fff', bgcolor: '#16a34a', ':hover': { bgcolor: '#15803d' } }}>
                                        <ArrowForwardIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    </LocalizationProvider>
                </Box>

                <Box
                    sx={{
                        width: '95%',
                        justifySelf: 'center',
                        alignSelf: 'start',
                        maxHeight: '100%',
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: '#fff',
                        border: '1px solid #b8b6b6',
                        borderRadius: 2,
                        overflow: 'hidden',
                        position: 'relative',
                    }}
                >
                    {isLoading && (
                        <Box sx={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.7)' }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}

                    {!isLoading && receipts.length === 0 && (
                        <Box sx={{ py: 6, textAlign: 'center', color: '#555' }}>
                            <Typography sx={{ fontWeight: 600 }}>No data found</Typography>
                            <Typography sx={{ fontSize: 13 }}>Select a date range and click Get Data.</Typography>
                        </Box>
                    )}

                    {receipts.length > 0 && (
                        <TableContainer sx={{ flex: '0 1 auto', minHeight: 0, overflow: 'auto', overscrollBehavior: 'contain' }}>
                            <Table stickyHeader size="small" sx={{ borderCollapse: 'separate' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ ...headCell, ...stickyLeft, zIndex: 5 }}>Month</TableCell>
                                        {remarkNames.map((name) => (
                                            <TableCell key={name} align="right" sx={headCell}>{name}</TableCell>
                                        ))}
                                        <TableCell align="right" sx={{ ...headCell, ...totalBorder, ...stickyRight, zIndex: 5, }}>Total Amount ₹</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {monthRows.map((monthRow) => (
                                        <TableRow key={monthRow.monthKey} sx={{ '&:hover td': { bgcolor: '#fafafa' } }}>
                                            <TableCell sx={{ ...cell, ...stickyLeft, zIndex: 1, bgcolor: '#fff', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                {monthRow.monthLabel}
                                            </TableCell>
                                            {remarkNames.map((name) => {
                                                const amount = monthRow.amountByRemark[name];
                                                return (
                                                    <TableCell key={name} align="right" sx={{ ...cell, ...number, color: amount ? '#000' : '#aaa' }}>
                                                        {amount ? formatAmount(amount) : '-'}
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell align="right" sx={{ ...cell, ...number, ...totalBorder, fontWeight: 700, ...stickyRight, zIndex: 1, }}>
                                                {monthRow.total ? formatAmount(monthRow.total) : '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>

                                <TableFooter>
                                    <TableRow>
                                        <TableCell sx={{ ...footCell, ...stickyLeft, zIndex: 4 }}>Grand Total</TableCell>
                                        {remarkNames.map((name) => (
                                            <TableCell key={name} align="right" sx={{ ...footCell, ...number }}>
                                                {formatAmount(grandTotal.amountByRemark[name])}
                                            </TableCell>
                                        ))}
                                        <TableCell align="right" sx={{ ...footCell, ...number, ...totalBorder, ...stickyRight, zIndex: 4 }}>
                                            {formatAmount(grandTotal.total)}
                                        </TableCell>
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </TableContainer>
                    )}
                </Box>

                <CollectionPrint
                    ref={printRef}
                    monthRows={monthRows}
                    remarkNames={remarkNames}
                    grandTotal={grandTotal}
                    fromDate={appliedFromDate}
                    toDate={appliedToDate}
                    propertyName={propertyName}
                />

            </Box>
        </>
    );
};

export default CollectionSummary;